import { useState, useEffect, useCallback } from 'react';
import { patientManagementService, Patient, PatientSearchParams } from '../services/patientManagementService';

export const usePatientManagement = () => {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPatients = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await patientManagementService.getPatients();
      setPatients(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load patients');
    } finally {
      setLoading(false);
    }
  }, []);

  const searchPatients = useCallback(async (params: PatientSearchParams) => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await patientManagementService.searchPatients(params);
      setPatients(data);
    } catch (err: any) {
      setError(err.message || 'Failed to search patients');
    } finally {
      setLoading(false);
    }
  }, []);

  const updatePatientStatus = useCallback(async (patientId: string, status: string) => {
    try {
      await patientManagementService.updatePatientStatus(patientId, status);
      // Reload patients to get updated data
      await loadPatients();
    } catch (err: any) {
      setError(err.message || 'Failed to update patient status');
      throw err;
    }
  }, [loadPatients]);

  const updatePatientNotes = useCallback(async (patientId: string, notes: string) => {
    try {
      await patientManagementService.updatePatientNotes(patientId, notes);
      // Reload patients to get updated data
      await loadPatients();
    } catch (err: any) {
      setError(err.message || 'Failed to update patient notes');
      throw err;
    }
  }, [loadPatients]);

  const removePatient = useCallback(async (patientId: string) => {
    try {
      await patientManagementService.removePatient(patientId);
      // Reload patients to get updated data
      await loadPatients();
    } catch (err: any) {
      setError(err.message || 'Failed to remove patient');
      throw err;
    }
  }, [loadPatients]);

  const getPatient = useCallback(async (patientId: string): Promise<Patient> => {
    try {
      return await patientManagementService.getPatient(patientId);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch patient');
      throw err;
    }
  }, []);

  // Load patients on mount
  useEffect(() => {
    loadPatients();
  }, [loadPatients]);

  return {
    patients,
    loading,
    error,
    loadPatients,
    searchPatients,
    updatePatientStatus,
    updatePatientNotes,
    removePatient,
    getPatient,
  };
};
